import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Plus, Edit, Trash2, Play, Pause, Zap, CalendarClock, RefreshCw, BookMarked } from 'lucide-react';
import { SortableHeader, useSort, ResetViewButton } from '../ui/SortableHeader';
import { DateText } from '../ui/week-badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  template_type: 'customer', name: '', customer_id: '', customer_name: '',
  frequency: 'monthly', day_of_month: 1, currency: 'PLN', vat_rate: 23,
  reverse_charge: false, payment_terms_days: 14, notes: '',
  line_items: [{ description: '', quantity: 1, unit: 'service', unit_price: 0 }],
};

const fmtCurrency = (amt, cur = 'PLN') => {
  if (amt == null) return '-';
  return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: cur }).format(amt);
};

const fmtDate = (d) => <DateText value={d} empty="-" />;

const freqKey = { monthly: 'recurringDlg.monthly', quarterly: 'recurringDlg.quarterly' };
const statusColors = { true: 'bg-green-100 text-green-800', false: 'bg-gray-100 text-gray-700' };

const RecurringTemplatesTab = ({ currentUser, customers = [] }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [savedItems, setSavedItems] = useState([]);
  const [showSavedPicker, setShowSavedPicker] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [sortKey, sortDir, toggleSort, comparator, resetSort] = useSort('next_generation', 'asc', 'recurring-templates');
  const handleSort = (key) => toggleSort(key);
  const isViewCustomized = sortKey !== 'next_generation' || sortDir !== 'asc';

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'manager';

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/recurring-templates`);
      setTemplates(res.data);
    } catch { toast.error(t('recurringDlg.failedToLoadRecurringTemplates')); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTemplates(); }, []);
  useEffect(() => {
    if (dialogOpen) {
      axios.get(`${API}/settings/saved-invoice-items`).then(r => setSavedItems(r.data)).catch(() => {});
    }
  }, [dialogOpen]);

  const openCreate = (type = 'customer') => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, template_type: type });
    setDialogOpen(true);
  };

  const openEdit = (tmpl) => {
    setEditingId(tmpl.id);
    setForm({
      template_type: tmpl.template_type || 'customer',
      name: tmpl.name || '', customer_id: tmpl.customer_id || '',
      customer_name: tmpl.customer_name || '',
      frequency: tmpl.frequency || 'monthly',
      day_of_month: tmpl.day_of_month || 1,
      currency: tmpl.currency || 'PLN', vat_rate: tmpl.vat_rate ?? 23,
      reverse_charge: tmpl.reverse_charge || false,
      payment_terms_days: tmpl.payment_terms_days || 14,
      notes: tmpl.notes || '',
      line_items: tmpl.line_items?.length ? tmpl.line_items : [{ description: '', quantity: 1, unit: 'service', unit_price: 0 }],
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(t('recurringDlg.templateNameIsRequired')); return; }
    if (form.template_type === 'customer' && !form.customer_id) { toast.error(t('recurringDlg.selectACustomer')); return; }
    if (!form.line_items.some(i => i.description.trim())) { toast.error(t('recurringDlg.addAtLeastOneLineItem')); return; }

    try {
      if (editingId) {
        await axios.put(`${API}/recurring-templates/${editingId}`, form);
        toast.success(t('recurringDlg.templateUpdated'));
      } else {
        await axios.post(`${API}/recurring-templates`, form);
        toast.success(t('recurringDlg.templateCreated'));
      }
      setDialogOpen(false);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save template'); }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/recurring-templates/${id}/toggle`);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to toggle template'); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/recurring-templates/${id}`);
      toast.success(t('recurringDlg.templateDeleted'));
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete template'); }
  };

  const handleGenerateNow = async (id) => {
    setGenerating(id);
    try {
      const res = await axios.post(`${API}/recurring-templates/${id}/generate`);
      toast.success(`Draft invoice ${res.data.invoice_number} generated!`);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate invoice'); }
    finally { setGenerating(null); }
  };

  const updateLineItem = (idx, field, value) => {
    setForm(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const addLineItem = () => {
    setForm(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', quantity: 1, unit: 'service', unit_price: 0 }]
    }));
  };

  const removeLineItem = (idx) => {
    if (form.line_items.length <= 1) return;
    setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const templateTotal = form.line_items.reduce((acc, item) => {
    const net = (item.quantity || 0) * (item.unit_price || 0);
    const vat = form.reverse_charge ? 0 : net * (form.vat_rate || 0) / 100;
    return acc + net + vat;
  }, 0);

  if (loading) return <div className="text-center py-8 text-slate-500">{t('recurringDlg.loadingTemplates')}</div>;

  return (
    <div className="space-y-6" data-testid="recurring-templates-tab">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">{t('recurringDlg.recurringInvoiceTemplates')}</h3>
          <p className="text-sm text-slate-500">{t('recurringDlg.autoGenerateDraftInvoicesOnA')}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button onClick={() => openCreate('customer')} size="sm" data-testid="create-customer-template-btn">
              <Plus className="w-4 h-4 mr-1" />{t('recurringDlg.customerTemplate')}
            </Button>
          )}
          <Button onClick={() => openCreate('employee')} variant="outline" size="sm" data-testid="create-employee-template-btn">
            <Plus className="w-4 h-4 mr-1" />{t('recurringDlg.employeeTemplate')}
          </Button>
        </div>
      </div>

      {templates.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <CalendarClock className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">{t('recurringDlg.noRecurringTemplatesYet')}</p>
            <p className="text-sm text-slate-400 mt-1">{t('recurringDlg.createOneToAutoGenerateDraft')}</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-0 shadow-sm">
          {isViewCustomized && (
            <div className="px-4 pt-3 flex justify-end">
              <ResetViewButton show onReset={resetSort} testId="reset-view-recurring-templates" />
            </div>
          )}
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.template')}</SortableHeader>
                  <SortableHeader column="template_type" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.type')}</SortableHeader>
                  <SortableHeader column="for" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.forColumn')}</SortableHeader>
                  <SortableHeader column="frequency" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.frequency')}</SortableHeader>
                  <SortableHeader column="next_generation" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.nextGeneration')}</SortableHeader>
                  <SortableHeader column="last_invoice" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('recurringDlg.lastInvoice')}</SortableHeader>
                  <SortableHeader column="amount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right">{t('recurringDlg.amount')}</SortableHeader>
                  <SortableHeader column="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>{t('common.status')}</SortableHeader>
                  <TableHead className="text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const computeTotal = (tmpl) => (tmpl.line_items || []).reduce((acc, item) => {
                    const net = (item.quantity || 0) * (item.unit_price || 0);
                    const vat = tmpl.reverse_charge ? 0 : net * (tmpl.vat_rate || 0) / 100;
                    return acc + net + vat;
                  }, 0);
                  return [...templates].sort(comparator((tmpl, key) => {
                    switch (key) {
                      case 'name': return (tmpl.name || '').toLowerCase();
                      case 'template_type': return tmpl.template_type || '';
                      case 'for': return ((tmpl.template_type === 'customer' ? tmpl.customer_name : tmpl.employee_name) || '').toLowerCase();
                      case 'frequency': return tmpl.frequency || '';
                      case 'next_generation':
                        return tmpl.is_active && tmpl.next_generation_date ? new Date(tmpl.next_generation_date).getTime() : Number.POSITIVE_INFINITY;
                      case 'last_invoice':
                        return tmpl.last_generated_at ? new Date(tmpl.last_generated_at).getTime() : 0;
                      case 'amount': return computeTotal(tmpl);
                      case 'status': return tmpl.is_active ? 1 : 0;
                      default: return '';
                    }
                  })).map(tmpl => {
                  const total = computeTotal(tmpl);
                  return (
                    <TableRow key={tmpl.id} data-testid={`template-row-${tmpl.id}`}>
                      <TableCell className="font-medium">{tmpl.name}</TableCell>
                      <TableCell>
                        <Badge variant={tmpl.template_type === 'customer' ? 'default' : 'secondary'}>
                          {tmpl.template_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {tmpl.template_type === 'customer' ? tmpl.customer_name : tmpl.employee_name}
                      </TableCell>
                      <TableCell>{freqKey[tmpl.frequency] ? t(freqKey[tmpl.frequency]) : tmpl.frequency}</TableCell>
                      <TableCell>
                        {tmpl.is_active ? (
                          <span className="text-sm">{fmtDate(tmpl.next_generation_date)}</span>
                        ) : <span className="text-slate-400 text-sm">{t('recurringDlg.paused')}</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {tmpl.last_invoice_number ? (
                          <span>{tmpl.last_invoice_number} <span className="text-slate-400">({fmtDate(tmpl.last_generated_at)})</span></span>
                        ) : <span className="text-slate-400">{t('recurringDlg.never')}</span>}
                      </TableCell>
                      <TableCell className="text-right font-medium">{fmtCurrency(total, tmpl.currency)}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[tmpl.is_active]}>
                          {tmpl.is_active ? t('recurringDlg.active') : t('recurringDlg.paused')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" title={tmpl.is_active ? t('recurringDlg.pause') : t('recurringDlg.resume')}
                            onClick={() => handleToggle(tmpl.id)} data-testid={`toggle-${tmpl.id}`}>
                            {tmpl.is_active ? <Pause className="w-4 h-4 text-orange-500" /> : <Play className="w-4 h-4 text-green-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" title={t('recurringDlg.generateNow')}
                            onClick={() => handleGenerateNow(tmpl.id)}
                            disabled={generating === tmpl.id} data-testid={`generate-${tmpl.id}`}>
                            {generating === tmpl.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 text-blue-600" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(tmpl)} data-testid={`edit-${tmpl.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`delete-${tmpl.id}`}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t('recurringDlg.deleteTemplate')}</AlertDialogTitle>
                                <AlertDialogDescription>Delete &quot;{tmpl.name}&quot;? Previously generated invoices will not be affected.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(tmpl.id)}>{t('common.delete')}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                  });
                })()}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? t('recurringDlg.editTemplateTitle') : t('recurringDlg.createTemplateTitle')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('recurringDlg.templateName')}</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder={t('recurringDlg.eGMonthlyConsulting')} data-testid="tmpl-name" />
              </div>
              <div className="space-y-2">
                <Label>{t('recurringDlg.type')}</Label>
                <Select value={form.template_type} onValueChange={v => setForm(p => ({ ...p, template_type: v }))}
                  disabled={!!editingId}>
                  <SelectTrigger data-testid="tmpl-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {isAdmin && <SelectItem value="customer">{t('recurringDlg.customerInvoice')}</SelectItem>}
                    <SelectItem value="employee">{t('recurringDlg.employeeInvoice')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.template_type === 'customer' && (
              <div className="space-y-2">
                <Label>{t('recurringDlg.customer')}</Label>
                <Select value={form.customer_id} onValueChange={v => {
                  const c = customers.find(x => x.id === v);
                  setForm(p => ({ ...p, customer_id: v, customer_name: c?.name || '' }));
                }}>
                  <SelectTrigger data-testid="tmpl-customer"><SelectValue placeholder={t('recurringDlg.selectCustomer')} /></SelectTrigger>
                  <SelectContent>
                    {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('recurringDlg.frequency')}</Label>
                <Select value={form.frequency} onValueChange={v => setForm(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger data-testid="tmpl-frequency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t('recurringDlg.monthly')}</SelectItem>
                    <SelectItem value="quarterly">{t('recurringDlg.quarterly')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('recurringDlg.dayOfMonth')}</Label>
                <Input type="number" min={1} max={28} value={form.day_of_month}
                  onChange={e => setForm(p => ({ ...p, day_of_month: parseInt(e.target.value) || 1 }))}
                  data-testid="tmpl-day" />
              </div>
              <div className="space-y-2">
                <Label>{t('recurringDlg.paymentTermsDays')}</Label>
                <Input type="number" value={form.payment_terms_days}
                  onChange={e => setForm(p => ({ ...p, payment_terms_days: parseInt(e.target.value) || 14 }))}
                  data-testid="tmpl-payment-terms" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('recurringDlg.currency')}</Label>
                <Select value={form.currency} onValueChange={v => setForm(p => ({ ...p, currency: v }))}>
                  <SelectTrigger data-testid="tmpl-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PLN">{t('auto.misc2.pln')}</SelectItem>
                    <SelectItem value="EUR">{t('auto.misc2.eur')}</SelectItem>
                    <SelectItem value="GBP">{t('auto.misc2.gbp')}</SelectItem>
                    <SelectItem value="USD">{t('auto.misc2.usd')}</SelectItem>
                    <SelectItem value="DKK">{t('auto.misc2.dkk')}</SelectItem>
                    <SelectItem value="NOK">{t('auto.misc2.nok')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('recurringDlg.vatRate')}</Label>
                <Input type="number" value={form.vat_rate}
                  onChange={e => setForm(p => ({ ...p, vat_rate: parseFloat(e.target.value) || 0 }))}
                  disabled={form.reverse_charge} data-testid="tmpl-vat" />
              </div>
              <div className="flex items-end space-x-2 pb-2">
                <Switch checked={form.reverse_charge}
                  onCheckedChange={v => setForm(p => ({ ...p, reverse_charge: v, vat_rate: v ? 0 : 23 }))}
                  data-testid="tmpl-reverse-charge" />
                <Label className="text-sm">{t('recurringDlg.reverseCharge')}</Label>
              </div>
            </div>

            {/* Line Items */}
            <div className="space-y-2">
              <div className="flex justify-between items-center gap-2">
                <Label className="text-sm font-medium">{t('recurringDlg.lineItems')}</Label>
                <div className="flex gap-1">
                  {savedItems.length > 0 && (
                    <div className="relative">
                      <Button variant="outline" size="sm" onClick={() => setShowSavedPicker(!showSavedPicker)} data-testid="tmpl-from-saved">
                        <BookMarked className="w-3 h-3 mr-1" /> {t('invoiceDlg.fromSaved')}
                      </Button>
                      {showSavedPicker && (
                        <div className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-50 w-64 max-h-48 overflow-y-auto">
                          {savedItems.map(item => (
                            <button key={item.id} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm border-b last:border-0"
                              onClick={() => {
                                setForm(prev => ({
                                  ...prev,
                                  line_items: [...prev.line_items.filter(i => i.description.trim()), {
                                    description: item.description,
                                    quantity: item.default_quantity || 1,
                                    unit: item.unit || 'service',
                                    unit_price: item.default_unit_price || 0,
                                  }]
                                }));
                                setShowSavedPicker(false);
                              }}>
                              <div className="font-medium">{item.name}</div>
                              <div className="text-xs text-slate-500 truncate">{item.description}</div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <Button variant="outline" size="sm" onClick={addLineItem} data-testid="tmpl-add-item">
                    <Plus className="w-3 h-3 mr-1" />{t('invoiceDlg.addItem')}
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                {form.line_items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2 items-end sm:grid-cols-12">
                    <div className="col-span-2 sm:col-span-5">
                      {idx === 0 && <Label className="text-xs text-slate-500">{t('recurringDlg.description')}</Label>}
                      <Input value={item.description} placeholder={t('recurringDlg.serviceDescription')}
                        onChange={e => updateLineItem(idx, 'description', e.target.value)}
                        data-testid={`tmpl-item-desc-${idx}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-500">{t('invoiceDlg.qty')}</Label>}
                      <Input type="number" value={item.quantity}
                        onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        data-testid={`tmpl-item-qty-${idx}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-500">{t('recurringDlg.unit')}</Label>}
                      <Input value={item.unit}
                        onChange={e => updateLineItem(idx, 'unit', e.target.value)}
                        data-testid={`tmpl-item-unit-${idx}`} />
                    </div>
                    <div className="col-span-1 sm:col-span-2">
                      {idx === 0 && <Label className="text-xs text-slate-500">{t('recurringDlg.unitPrice')}</Label>}
                      <Input type="number" value={item.unit_price}
                        onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        data-testid={`tmpl-item-price-${idx}`} />
                    </div>
                    <div className="col-span-2 flex justify-center sm:col-span-1">
                      {form.line_items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeLineItem(idx)} className="text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-right text-sm font-medium text-slate-700 pt-1" data-testid="tmpl-total">
                {t('recurringDlg.estTotal')} {fmtCurrency(templateTotal, form.currency)}
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('recurringDlg.notesOptional')}</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder={t('recurringDlg.notesToIncludeOnGeneratedInvoices')} data-testid="tmpl-notes" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-btn">{t('common.cancel')}</Button>
            <Button onClick={handleSave} data-testid="tmpl-save-btn">
              {editingId ? t('recurringDlg.updateTemplate') : t('recurringDlg.createTemplate')}
            </Button>
          </DialogFooter>
          {!editingId && (
            <p className="text-xs text-slate-400 text-center -mt-2">{t('recurringDlg.templatesAreCreatedAsPausedActivate')}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecurringTemplatesTab;
